import React, { useState, useEffect } from 'react';
import { Upload, Card, Input, Button, Modal, message, Space, Typography, Row, Col } from 'antd';
import {
  CalendarOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  EyeOutlined,
  DeleteOutlined,
  UploadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { DeleteVisitDoc, uploadSiteVisitDocs } from '@app/api/prImportFreeSales.api';

const { Title, Text } = Typography;

type Document = {
  id: number;
  product_registration_id: number;
  status: string;
  document_id?: string;
  name: string;
  file: string;
  varified?: boolean;
  verified?: boolean;
  user_id?: number;
  created_at?: string;
  updated_at?: string;
  url?: string;
};

type SiteVisit = {
  id: number;
  title: string;
  status: string;
  documents: Document[];
  canUpload: boolean;
};

interface SiteVisitsTabProps {
  status: string | null;
  pr_id: string | null;
  visitDocs: Record<string, Document[]>;
}

const SiteVisitsTab: React.FC<SiteVisitsTabProps> = ({ pr_id, status, visitDocs = {} }) => {
  const [siteVisits, setSiteVisits] = useState<SiteVisit[]>([
    {
      id: 1,
      title: 'Site Visit Phase 1',
      status: 'PENDING_VISIT',
      documents: [],
      canUpload: false,
    },
    {
      id: 2,
      title: 'Site Visit Phase 2',
      status: 'PENDING_SECOND_VISIT',
      documents: [],
      canUpload: false,
    },
    {
      id: 3,
      title: 'Site Visit Phase 3',
      status: 'PENDING_THIRD_VISIT',
      documents: [],
      canUpload: false,
    },
  ]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentVisitId, setCurrentVisitId] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentName, setDocumentName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [reuploadDocumentId, setReuploadDocumentId] = useState<number | null>(null);
  const [isReuploading, setIsReuploading] = useState(false);

  console.log('Passed Visit Documents:', visitDocs);

  useEffect(() => {
    console.log('Visit Docs Structure:', visitDocs);

    setSiteVisits((prev) =>
      prev.map((visit) => ({
        ...visit,
        canUpload: status === visit.status,
        documents: visitDocs[visit.status] || [],
      })),
    );
  }, [status, visitDocs]);

  const handleFileUpload = async (visitId: number, files: File[], customName: string, documentId?: number) => {
    if (!customName || !customName.trim()) {
      message.error('Please provide a document name');
      return;
    }

    if (!pr_id) {
      message.error('Product registration ID is required');
      return;
    }

    setUploading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        const fileName = files.length > 1 ? `${customName.trim()} (${i + 1})` : customName.trim();

        formData.append('file', file);
        formData.append('name', fileName);
        formData.append('id', documentId ? documentId.toString() : '');

        const response = await uploadSiteVisitDocs(parseInt(pr_id), formData);

        if (response.data) {
          if (documentId) {
            // Replace existing document
            setSiteVisits((prev) =>
              prev.map((visit) =>
                visit.status === status
                  ? {
                      ...visit,
                      documents: visit.documents.map((doc) =>
                        doc.id === documentId ? (response.data as any as Document) : doc,
                      ),
                    }
                  : visit,
              ),
            );
          } else {
            // Add new document
            setSiteVisits((prev) =>
              prev.map((visit) =>
                visit.status === status
                  ? {
                      ...visit,
                      documents: [...visit.documents, response.data as any as Document],
                    }
                  : visit,
              ),
            );
          }
        }
      }

      const action = documentId ? 'updated' : 'uploaded';
      message.success(`${files.length} document(s) ${action} successfully`);
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error(error.message || 'Failed to upload document(s)');
    } finally {
      setUploading(false);
    }
  };

  const handleModalOk = async () => {
    if (!documentName.trim()) {
      message.error('Please enter a document name');
      return;
    }

    if (currentVisitId) {
      await handleFileUpload(currentVisitId, selectedFiles, documentName, reuploadDocumentId || undefined);
      setIsModalVisible(false);
      setDocumentName('');
      setSelectedFiles([]);
      setCurrentVisitId(null);
      setReuploadDocumentId(null);
      setIsReuploading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setDocumentName('');
    setSelectedFiles([]);
    setCurrentVisitId(null);
    setReuploadDocumentId(null);
    setIsReuploading(false);
  };

  const showNameModal = (visitId: number, files: File[], documentId?: number, existingName?: string) => {
    setCurrentVisitId(visitId);
    setSelectedFiles(files);
    setIsModalVisible(true);
    setReuploadDocumentId(documentId || null);
    setIsReuploading(!!documentId);

    if (existingName) {
      setDocumentName(existingName);
    } else if (files.length === 1) {
      const nameWithoutExt = files[0].name.replace(/\.[^/.]+$/, '');
      setDocumentName(nameWithoutExt);
    } else {
      setDocumentName('');
    }
  };

  const reuploadDocument = (visitId: number, document: Document) => {
    const input = window.document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,image/*';
    input.multiple = false;

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const validTypes = ['application/pdf'];
        const isValidImage = file.type.startsWith('image/');
        const isValidPDF = validTypes.includes(file.type);

        if (!isValidPDF && !isValidImage) {
          message.error('Only PDF and image files are supported');
          return;
        }

        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
          message.error('File must be smaller than 2MB');
          return;
        }

        if (file.name.length > 80) {
          message.error('File name must not exceed 80 characters');
          return;
        }

        showNameModal(visitId, [file], document.id, document.name);
      }
    };

    input.click();
  };

  const removeDocument = async (documentId: number) => {
    if (!pr_id) {
      message.error('Product registration ID is required');
      return;
    }

    try {
      await DeleteVisitDoc(parseInt(pr_id), documentId);

      setSiteVisits((prev) =>
        prev.map((visit) => ({
          ...visit,
          documents: visit.documents.filter((doc) => doc.id !== documentId),
        })),
      );

      message.success('Document removed successfully');
    } catch (error: any) {
      console.error('Delete error:', error);
      message.error(error.message || 'Failed to remove document');
    }
  };

  const viewDocument = (document: Document) => {
    const documentUrl = document.url || `/storage/uploads/${document.file}`;
    window.open(documentUrl, '_blank');
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '14px' }} />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
      return <FileImageOutlined style={{ color: '#1890ff', fontSize: '14px' }} />;
    }
    return <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />;
  };

  const uploadProps = (visit: SiteVisit) => ({
    name: 'file',
    multiple: true,
    accept: '.pdf,image/*',
    beforeUpload: (file: File, fileList: File[]) => {
      const validTypes = ['application/pdf'];
      const isValidImage = file.type.startsWith('image/');
      const isValidPDF = validTypes.includes(file.type);

      if (!isValidPDF && !isValidImage) {
        message.error('Only PDF and image files are supported');
        return false;
      }

      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('File must be smaller than 2MB');
        return false;
      }

      if (file.name.length > 80) {
        message.error('File name must not exceed 80 characters');
        return false;
      }

      showNameModal(visit.id, fileList);
      return false; // Prevent default upload
    },
    showUploadList: false,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const isDocumentVerified = (doc: Document) => {
    return doc.varified || doc.verified || false;
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Title level={5} style={{ marginBottom: '4px' }}>
          Site Visits Documentation
        </Title>
        <Text type="secondary">Upload and manage documents for each site visit phase.</Text>
      </div>

      <Row gutter={16}>
        {siteVisits.map((visit) => (
          <Col key={visit.id} xs={24} sm={24} md={8} lg={8} xl={8}>
            <Card
              size="small"
              style={{
                height: '100%',
                minHeight: '200px',
                opacity: visit.canUpload ? 1 : 0.6,
              }}
              bodyStyle={{ padding: '16px' }}
            >
              {/* Header */}
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    marginBottom: '8px',
                  }}
                >
                  <CalendarOutlined style={{ color: '#8C1C03', fontSize: '16px' }} />
                  <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
                    {visit.title}
                  </Title>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {visit.documents.length} document{visit.documents.length !== 1 ? 's' : ''}
                </Text>
                {!visit.canUpload && (
                  <div style={{ marginTop: '4px' }}>
                    <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                      Upload not available for this phase
                    </Text>
                  </div>
                )}
              </div>

              {visit.canUpload && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <Upload {...uploadProps(visit)}>
                    <Button
                      icon={<UploadOutlined />}
                      type="primary"
                      size="small"
                      block
                      loading={uploading}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </Upload>
                </div>
              )}

              {visit.documents.length > 0 && (
                <div>
                  <div
                    style={{
                      borderTop: '1px solid #f0f0f0',
                      paddingTop: '12px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {visit.documents.map((doc) => (
                        <div
                          key={doc.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            padding: '6px 8px',
                            backgroundColor: isDocumentVerified(doc) ? '#f6ffed' : '#f9f9f9',
                            borderRadius: '4px',
                            border: `1px solid ${isDocumentVerified(doc) ? '#b7eb8f' : '#f0f0f0'}`,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flex: 1, minWidth: 0 }}>
                            <div style={{ marginTop: '2px' }}>{getFileIcon(doc.file)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  lineHeight: '16px',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {doc.name}
                              </div>
                              <div
                                style={{
                                  fontSize: '10px',
                                  color: '#8c8c8c',
                                  lineHeight: '14px',
                                  marginTop: '2px',
                                }}
                              >
                                {formatDate(doc.created_at)}
                                {isDocumentVerified(doc) && (
                                  <span style={{ color: '#52c41a', marginLeft: '4px' }}>• Verified</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '2px', marginLeft: '4px' }}>
                            <Button
                              type="text"
                              size="small"
                              icon={<EyeOutlined style={{ fontSize: '14px' }} />}
                              onClick={() => viewDocument(doc)}
                              title="View Document"
                              style={{ padding: '2px 4px', minWidth: 'auto' }}
                            />
                            {visit.canUpload && (
                              <>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<ReloadOutlined style={{ fontSize: '14px' }} />}
                                  onClick={() => reuploadDocument(visit.id, doc)}
                                  title="Re-upload Document"
                                  style={{ padding: '2px 4px', minWidth: 'auto' }}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined style={{ fontSize: '14px' }} />}
                                  onClick={() => removeDocument(doc.id)}
                                  title="Remove Document"
                                  style={{ padding: '2px 4px', minWidth: 'auto' }}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </Space>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        title={isReuploading ? 'Replace Document' : 'Enter Document Name'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={uploading ? (isReuploading ? 'Replacing...' : 'Uploading...') : isReuploading ? 'Replace' : 'Upload'}
        cancelText="Cancel"
        confirmLoading={uploading}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            {isReuploading
              ? `Replace document with: ${selectedFiles[0]?.name}`
              : selectedFiles.length === 1
              ? `Enter a name for: ${selectedFiles[0]?.name}`
              : `Enter a name for ${selectedFiles.length} files`}
          </Text>
        </div>
        <Input
          placeholder="Enter document name..."
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          onPressEnter={handleModalOk}
          autoFocus
          disabled={uploading}
          maxLength={100}
          showCount
        />
      </Modal>
    </div>
  );
};

export default SiteVisitsTab;
